import { useState } from 'react';
import { Button, Field, Panel, Pill, ScreenHeader, StateBox, WeakestLinkBanner } from '@garnet/ui';
import { humanize } from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import type { PortalApi } from '../data/types.js';
import { Drop } from '../components/Drop.js';

export function Respond({ api }: { api: PortalApi }) {
  const { data, loading, error } = useAsync(() => api.getFlag());
  const [evidenceUp, setEvidenceUp] = useState(false);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);

  if (loading) return <StateBox kind="loading" title="Loading the open flag" />;
  if (error || !data) return <StateBox kind="error" title="Could not load the flag">{error}</StateBox>;

  return (
    <div className="screen">
      <ScreenHeader
        eyebrow="Respond to flag"
        title="Respond to a flag"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Pill state="flag">{data.check}</Pill>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
              {humanize(data.flag_type)} · {data.severity} · {data.scope}-scope
            </span>
          </div>
        }
      />

      <WeakestLinkBanner tone="flag">
        <strong>{data.detail}</strong>
        {data.affected_units.length > 0 && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}> Affected units: {data.affected_units.join(', ')}.</span>
        )}{' '}
        {data.ask}
      </WeakestLinkBanner>

      {sent ? (
        <Panel className="pad">
          <div className="weak" style={{ borderLeftColor: 'var(--verified)', background: 'var(--verified-bg)' }}>
            <span className="wl" style={{ color: 'var(--verified-ink)' }}>sent</span>
            <span className="wt">Your response and evidence were submitted. The case moves to review with the buyer.</span>
          </div>
        </Panel>
      ) : (
        <Panel className="pad">
          <div className="eyebrow-sm" style={{ marginBottom: 14 }}>Your response</div>
          <div style={{ display: 'grid', gap: 12 }}>
            <Drop
              label="Manufacturer-callback evidence"
              hint="Confirmation the units were produced and sold to your custody"
              done={evidenceUp}
              onDrop={() => setEvidenceUp(true)}
            />
            <Field label="Response">
              <textarea
                className="input"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Explain the custody of the affected units…"
                style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
              />
            </Field>
            <div>
              <Button variant="primary" icon="check" disabled={!evidenceUp} onClick={() => setSent(true)}>
                Submit response
              </Button>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}
